import { createSlice } from '@reduxjs/toolkit';
import { ROLES } from 'constant/enums';
import { getRolesKeyByValue } from 'utils/utils';
import { decodeToken } from 'utils/DecodeToken';

const localStorageKey = 'user';

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    readOnly: localStorage.getItem('readOnly') || false,
    authExpireTime: localStorage.getItem('expireTime') || false,
    showDisclaimer: localStorage.getItem('showDisclaimer') || false,
    // state use for access the admin view from super admin role
    adminAccess: localStorage.getItem('adminAccess') || false,
    storeData: JSON.parse(localStorage.getItem('storeData')) || {},
    projectData: JSON.parse(localStorage.getItem('projectData')) || {},
    roleBeforeAccess: localStorage.getItem('roleBeforeAccess') || null,
    authToken: localStorage.getItem('token') || null,
    userData: JSON.parse(localStorage.getItem('user')) || {},
    loggedOut: false,
    publishAssessments: [],
    allUsers: []
  },
  reducers: {
    addReadOnly: (state) => {
      state.readOnly = true;
      localStorage.setItem('readOnly', true);
    },

    removeReadOnly: (state) => {
      state.readOnly = false;
      localStorage.removeItem('readOnly');
    },

    addAuthExpireTime: (state) => {
      const decodedToken = decodeToken(state.authToken);

      let expireTime = decodedToken?.exp;
      expireTime -= 60;
      state.authExpireTime = expireTime;

      localStorage.setItem('expireTime', state.authExpireTime);
    },

    addShowDisclaimer: (state, action) => {
      state.showDisclaimer = action.payload;
      localStorage.setItem('showDisclaimer', true);
    },

    removeShowDisclaimer: (state) => {
      state.showDisclaimer = false;
      localStorage.setItem('showDisclaimer', false);
      localStorage.removeItem('showDisclaimer');
    },

    loggedIn: (state, action) => {
      state.authToken = action.payload;
      localStorage.setItem('token', action.payload);
    },

    // add super admin store id
    addRole: (state, action) => {
      state.roleBeforeAccess = action.payload;
      localStorage.setItem('roleBeforeAccess', action.payload);
    },

    loggedOut: (state) => {
      state.authToken = null;
      state.loggedOut = true;
      state.adminAccess = false;
      state.roleBeforeAccess = null;
      state.userData = null;
      state.storeData = {};
      state.projectData = null;
      state.showDisclaimer = false;
      state.authExpireTime = false;
      state.readOnly = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('redirectAfterLogin');
      localStorage.removeItem('roleBeforeAccess');
      localStorage.removeItem('storeData');
      localStorage.removeItem('projectData');
      localStorage.removeItem('adminAccess');
      localStorage.removeItem('showDisclaimer');
      localStorage.removeItem('expireTime');
      localStorage.removeItem('readOnly');
    },

    addUser: (state, action) => {
      localStorage.setItem('user', JSON.stringify(action.payload));
      state.userData = JSON.parse(localStorage.getItem('user'));
    },

    updateUser: (state, action) => {
      const existingData = localStorage.getItem(localStorageKey);
      const existingDataJSON = existingData ? JSON.parse(existingData) : {};
      const { avatar, firstName, lastName } = action.payload;
      const data = { ...existingDataJSON, avatar: avatar, fullName: lastName + ' ' + firstName };
      state.userData = data;
      localStorage.setItem(localStorageKey, JSON.stringify(data));
    },

    // get Admin access from super admin role
    // update storeId and role
    addAdminAccess: (state, action) => {
      const existingUserData = JSON.parse(localStorage.getItem(localStorageKey));

      const data = {
        ...existingUserData,

        storeId: action.payload,
        roles: [getRolesKeyByValue(ROLES.ROLE_ADMIN)]
      };

      state.userData = data;
      localStorage.setItem(localStorageKey, JSON.stringify(data));

      state.adminAccess = true;
      localStorage.setItem('adminAccess', true);
    },

    // Back to super admin access
    // updating role and store id
    removeAdminAccess: (state) => {
      const existingUserData = JSON.parse(localStorage.getItem(localStorageKey));

      const data = {
        ...existingUserData,

        roles: [state.roleBeforeAccess]
      };

      state.userData = data;
      localStorage.setItem(localStorageKey, JSON.stringify(data));

      state.adminAccess = false;
      localStorage.removeItem('adminAccess');

      state.storeData = null;
      state.projectData = null;
      localStorage.removeItem('storeData');
      localStorage.removeItem('projectData');
    },

    addStore: (state, action) => {
      state.storeData = action.payload;
      localStorage.setItem('storeData', JSON.stringify(action.payload));
    },

    addProject: (state, action) => {
      state.projectData = action.payload;
      localStorage.setItem('projectData', JSON.stringify(action.payload));
    },

    removeProject: (state, action) => {
      if (state.projectData === action.payload || state.projectData?.id === action.payload) {
        state.projectData = null;
        localStorage.removeItem('projectData');
      }
    },

    removeUser: (state) => {
      state.userData = null;
      localStorage.removeItem('user');
    },

    addAssessment: (state, action) => {
      state.publishAssessments = action.payload;
    },

    addUsers: (state, action) => {
      state.allUsers = action.payload;
      state.allUsers = state.allUsers?.filter((user) => user.active === true);
    }
  }
});

export const {
  loggedIn,
  loggedOut,
  addUser,
  removeUser,
  addProject,
  removeProject,
  addAssessment,
  addUsers,
  updateUser,
  addAdminAccess,
  removeAdminAccess,
  addRole,
  addStore,
  addShowDisclaimer,
  removeShowDisclaimer,
  addAuthExpireTime,
  addReadOnly,
  removeReadOnly
} = authSlice.actions;
export default authSlice.reducer;
